// import * as Config from '../../../../config';
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from "react-redux";

import FixedSizeList from './FixedSizeList';
import Linkage from '../../common/Linkage';
import BlockLoading from '../../common/BlockLoading';
import Next from '../../pagination/Next';
import Prev from '../../pagination/Prev';

function ListColumns({
  id, pathApi, title = null, titleClassName, subTitle = null, subTitleClassName, width, height, columnCount = 1, rowCount = 1, size, rowType = `Column`,
  hasPagination = false, allTo = null, allLabel = `View All`, style, enableArrows = false, autoFirstLoadCondition = null,
  linkToUID = null, customFormatData = null, from = null, cache = true, paginationLimit = null, oneRequestStore = null,
  children
}) {
  const gap = 16;
  const widthByColumn = (width - ((columnCount - 1) * gap)) / columnCount;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = paginationLimit ? Math.ceil(paginationLimit / (columnCount * rowCount)) : null;
  const [isLastPage, setIsLastPage] = useState(false);

  const buildColumns = useCallback((page) => (
    [...new Array(columnCount)].map((_, i) => ({
      index: i,
      pathApi: (pathApi && oneRequestStore === null) ? `${pathApi}${pathApi.includes(`?`) ? '&' : '?'}per_page=${rowCount}&page=${(page - 1) * columnCount + i + 1}` : null,
      data: oneRequestStore ? oneRequestStore.slice(((page - 1) * columnCount * rowCount) + i * rowCount, ((page - 1) * columnCount * rowCount) + (i + 1) * rowCount) : [],
      id: `${id}-[${i}]-p${page}`,
      visible: true
    }))
  ), [columnCount, pathApi, rowCount, id, oneRequestStore]);
  const [columns, setColumns] = useState(() => buildColumns(1));

  const goToPage = (page, lastPage = false) => {
    setColumns(buildColumns(page));
    setCurrentPage(page);
    setIsLastPage(lastPage);
  }

  // Optimisation One Request From Automix
  const stack = useSelector( state => state.stackAutomix);
  useEffect(() => {
    if (linkToUID) {
      const data = (Array.isArray(stack)) ? stack.find( x => x.uid === linkToUID ) : ((stack.uid === linkToUID) ? stack : null)
      const offset = (currentPage - 1) * columnCount * rowCount;
      setColumns(columns.map( (x,i) => {
        x.data = (data) ? data.tracks.slice(offset + i * rowCount, offset + (i + 1) * rowCount) : [];
        if (data) {
          setIsLastPage(offset + (i + 1) * rowCount >= data.tracks.length);
        }
        return x;
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkToUID, stack, rowCount, currentPage])
  const isDataReady = columns.every( x => x.data.length > 0 ) || linkToUID === null;

  const [visible, setVisible] = useState(true);
  const callbackEmpty = (id) => {
    let column = columns.find(x => x.id === id);
    if (column) {
      column.visible = false;
      if (column.index === columnCount - 1) {
        setIsLastPage(true);
      }
    } else {
      goToPage(currentPage - 1, true); // Bug from API, some requests with pagination break - client hotfix
    }
  }
  const checkVisibility = () => {
    const allow = columns.reduce( (visible, column, i) => {
      if (i === 0 && !column.visible) {
        return false
      } else if (i > 0 && !columns[i - 1].visible && column.visible) {
        return false
      }
      return visible;
    }, true)
    if (!allow) {
      setVisible(false);
    }
  }

  const canGoPrev = currentPage > 1;
  // const effectiveIsLastPage = oneRequest && oneRequestStore.itemsFull
  //   ? offset + columnCount * rowCount >= oneRequestStore.itemsFull.length
  //   : isLastPage;
  const canGoNext = totalPages ? currentPage < totalPages : !isLastPage;
  const extraMargin = 2 * 6; // Row with margin:3

  return visible ? (
    <div style={{...style}}>
      <div className={`flexRow list-columns-headers`}>
        { title
          ? <span className={`bp-h5 type-regular type-light`}>
              <span className={titleClassName}>{title}</span>
              {subTitle && <span className={subTitleClassName}>{subTitle}</span> }
              { React.Children.map(children, child => (React.isValidElement(child)) && React.cloneElement(child)) }
              { hasPagination && <Linkage label={allLabel} to={allTo} style={{zIndex: 10, paddingLeft: 8}} linkClassName={`pag-all type-medium fz13`} /> }
            </span>
          : null
        }
        { hasPagination ? (
          <div className={`flexRow list-columns-pagination`} style={{marginTop: 40, marginLeft: 8}}>
            <div className={`flexRow list-columns-pagination-controls`}>
              <Prev active={canGoPrev} onClick={() => goToPage(currentPage - 1)} />
              <Next active={canGoNext} onClick={() => goToPage(currentPage + 1)} /> 
            </div>
          </div>
        ) : null }
      </div>
      <div className={`flexRow list-columns-container`}>
        { (linkToUID && !isDataReady) && <BlockLoading height={height + extraMargin} style={{ marginTop: 12 }} /> }
        { isDataReady && columns.map( (column, i) => (
            <FixedSizeList
              key={`listColumn-${column.id}`}
              id={column.id}
              width={widthByColumn}
              height={height + extraMargin}
              cache={cache}

              pathApi={(!linkToUID && oneRequestStore === null) ? column.pathApi : null}
              data={(linkToUID || oneRequestStore) ? column.data : null}
              customFormatData={customFormatData}
              from={from}

              rowSize={size}
              rowType={rowType}
              sortable={false}
              enableArrows={enableArrows}

              style={{}}
              styleLoading={{ marginLeft: 15, marginTop: 12 }}
              showLoading={(i === 0)}

              callbackEmpty={callbackEmpty}
              checkVisibility={checkVisibility}

              autoFirstLoadCondition={autoFirstLoadCondition}
            />
          ) )
        }
      </div>
    </div>
  ) : null
}

ListColumns.propTypes = {
  id: PropTypes.any.isRequired,
  pathApi: PropTypes.string,
  title: PropTypes.string,
  titleClassName: PropTypes.string,
  subTitle: PropTypes.string,
  subTitleClassName: PropTypes.string,
  allLabel: PropTypes.string,
  allTo: PropTypes.string,
  width: PropTypes.any.isRequired,
  height: PropTypes.any.isRequired,
  size: PropTypes.number.isRequired,
  rowType: PropTypes.string.isRequired,
  enableArrows: PropTypes.bool,
  style: PropTypes.object,
  oneRequestStore: PropTypes.array
}

export default React.memo(ListColumns); // IF BAD PERFORMANCE REMOVE
